import { Main as Container } from 'Components/Main';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { statusControl, maturityLevelKeys, translateDomain } from '../../utils/labels_utils';
import ChartLine from '../LineChart';
import { PageHeader } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BG = 'var(--vo-mist)';
const DIVIDER = 'var(--vo-cloud)';
const GREEN = 'var(--vo-success)';
const CYAN = 'var(--vo-capri)';
const TEXT = 'var(--vo-ink-2)';
const FADED = 'var(--vo-steel)';
const SUB_HEADER_BG = 'var(--vo-capri-100)';
const SUB_ROW_BG = 'var(--vo-capri-50)';

/* ── Page header ── */
const BackBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  &:hover { background: ${BG}; }
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ExportWrap = styled.div`
  position: relative;
`;
const ExportBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: ${BLUE_DEEP}; }
`;
const ExportDrop = styled.div`
  display: ${({ $open }) => $open ? 'block' : 'none'};
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--vo-escuro) 16%, transparent);
  border: 1px solid ${DIVIDER};
  z-index: 200;
  min-width: 190px;
  overflow: hidden;
`;
const ExportItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  color: ${INK};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  &:hover { background: ${BG}; }
`;

/* ── Grid layouts ── */
const DashGrid1 = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;
const DashGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 260px;
  gap: 16px;
  margin-bottom: 16px;
`;
const DashGrid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;
const ColStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* ── Card base ── */
const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;

/* ── KPI card ── */
const KpiNumber = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin: 0 0 6px;
  letter-spacing: -0.02em;
`;
const KpiTrack = styled.div`
  height: 8px;
  background: ${DIVIDER};
  border-radius: 4px;
  overflow: hidden;
`;
const KpiFill = styled.div`
  height: 100%;
  background: ${({ $gradient }) => $gradient};
  border-radius: 4px;
  width: ${({ $pct }) => Math.min(Number($pct) || 0, 100)}%;
`;
const KpiSubLabel = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 8px 0 0;
`;

/* ── Framework bars ── */
const BarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const BarRowHead = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`;
const BarRowName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${TEXT};
`;
const BarRowPct = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BLUE};
`;
const BarTrack = styled.div`
  height: 8px;
  background: ${DIVIDER};
  border-radius: 4px;
  overflow: hidden;
`;
const BarGradFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, ${BLUE_DEEP}, ${CYAN});
  width: ${({ $pct }) => Math.min(Number($pct) || 0, 100)}%;
`;

/* ── Donut ── */
const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;
const DonutLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;
const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  overflow: hidden;
`;
const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;
const LegendText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ── Maturity bars ── */
const MaturityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const MatRowHead = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
`;
const MatLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const MatCount = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`;
const MatTrack = styled.div`
  height: 5px;
  background: ${DIVIDER};
  border-radius: 3px;
  overflow: hidden;
`;
const MatFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  width: ${({ $pct }) => Math.min(Number($pct) || 0, 100)}%;
`;

/* ── Stacked bars ── */
const StackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const StackRowHead = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;
const StackName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
`;
const StackCount = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  color: ${MUTED};
`;
const StackBarWrap = styled.div`
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  gap: 1px;
`;
const StackSeg = styled.div`
  flex: ${({ $flex }) => $flex};
  background: ${({ $color }) => $color};
`;
const StackLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;
const StackLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  color: ${FADED};
`;
const StackDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── Unique entities ── */
const UniqueNumber = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 40px;
  font-weight: 800;
  color: ${BLUE_DEEP};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.1;
`;
const UniqueSubLabel = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 6px 0 0;
`;

/* ── Entity progress mini rows ── */
const EpTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 12px;
`;
const EpList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const EpRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: ${BG};
  border-radius: 8px;
  gap: 8px;
`;
const EpLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${TEXT};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EpRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;
const EpMiniTrack = styled.div`
  width: 60px;
  height: 5px;
  background: ${DIVIDER};
  border-radius: 3px;
  overflow: hidden;
`;
const EpMiniFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, ${BLUE_DEEP}, ${CYAN});
  width: ${({ $pct }) => Math.min(Number($pct) || 0, 100)}%;
`;
const EpPct = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE};
`;

/* ── Row 4: entity table card ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
  margin-bottom: 40px;
`;
const TableCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${DIVIDER};
  gap: 12px;
  flex-wrap: wrap;
`;
const TableCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;

/* ── Entity pill tabs ── */
const EntityTabsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${BG};
  border-radius: 10px;
  padding: 4px;
`;
const EntityTabBtn = styled.button`
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => $active ? 'white' : 'transparent'};
  color: ${({ $active }) => $active ? BLUE_DEEP : MUTED};
  box-shadow: ${({ $active }) => $active ? '0 1px 3px color-mix(in srgb, var(--vo-escuro) 10%, transparent)' : 'none'};
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  &:hover { background: ${({ $active }) => $active ? 'white' : DIVIDER}; }
`;

/* ── Entity expandable table ── */
const EtTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const EtTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .06em;
  padding: 10px 12px;
  border-bottom: 1px solid ${DIVIDER};
  text-align: left;
  background: white;
`;
const EtParentTr = styled.tr`
  background: var(--vo-mist);
  cursor: pointer;
  &:hover { background: ${BG}; }
  td { border-bottom: 1px solid ${DIVIDER}; }
`;
const EtTd = styled.td`
  padding: 12px 12px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
`;
const EtExpandBtn = styled.button`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--vo-line);
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: ${MUTED};
  flex-shrink: 0;
  &:hover { background: ${BG}; }
`;
const EtTypeChip = styled.span`
  display: inline-block;
  background: var(--vo-capri-50);
  color: ${BLUE_DEEP};
  border-radius: 6px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
`;
const EtProgressTrack = styled.div`
  flex: 1;
  height: 6px;
  background: ${DIVIDER};
  border-radius: 3px;
  overflow: hidden;
`;
const EtProgressFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, ${BLUE_DEEP}, ${CYAN});
  width: ${({ $pct }) => Math.min(Number($pct) || 0, 100)}%;
`;
const EtProgressPct = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BLUE};
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
`;

/* ── Sub-table (expanded entity controls) ── */
const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const SubHeadTr = styled.tr`
  background: ${SUB_HEADER_BG};
`;
const SubTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  text-transform: uppercase;
  letter-spacing: .06em;
  padding: 9px 12px;
  text-align: left;
`;
const SubDataTr = styled.tr`
  background: ${SUB_ROW_BG};
  &:hover { background: var(--vo-capri-50); }
  td { border-bottom: 1px solid ${DIVIDER}; }
`;
const SubTd = styled.td`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  padding: 9px 12px;
`;
const SubDomainChip = styled.span`
  display: inline-block;
  background: var(--vo-capri-50);
  color: ${BLUE_DEEP};
  border-radius: 4px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
`;
const SubStatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;
const SubViewBtn = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: ${MUTED};
  margin: 0 auto;
  &:hover { background: ${BG}; }
`;

/* ── Snapshot banner ── */
const SnapBanner = styled.div`
  background: linear-gradient(135deg, var(--vo-escuro), var(--vo-escuro-2));
  border-radius: 14px;
  padding: 16px 24px;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--vo-escuro) 20%, transparent);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
const SnapLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const SnapIconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: color-mix(in srgb, var(--vo-capri) 20%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const SnapBadge = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: color-mix(in srgb, var(--vo-white) 60%, transparent);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 3px;
`;
const SnapName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin: 0;
`;
const SnapRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  flex-shrink: 0;
`;
const SnapDateLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: color-mix(in srgb, var(--vo-white) 50%, transparent);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0;
`;
const SnapDateValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--vo-capri);
  margin: 0;
`;

const SNAP_LOCALE_MAP = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

/* ── Helpers ── */
const computeDonut = (items, r) => {
  const circ = 2 * Math.PI * r;
  const total = items.reduce((s, d) => s + (d.value || 0), 0);
  let cum = 0;
  return items.map(d => {
    const len = total > 0 ? ((d.value || 0) / total) * circ : 0;
    const seg = { ...d, da: `${len.toFixed(1)} ${(circ + 0.5).toFixed(1)}`, off: -cum };
    cum += len;
    return seg;
  });
};

const getStatusLabel = (key, t) => {
  const sc = statusControl[key];
  if (!sc) return key;
  if (sc.labelKey) return t(sc.labelKey);
  return sc.label || key;
};

export const GapDashboardView = ({
  title,
  segments,
  backLabel,
  onBack,
  overallProgress,
  linkedControls,
  totalControls,
  historicalProgress,
  frameworkData,
  statusDonutData,
  maturityData,
  entityDonutData,
  stackedData,
  entityProgressData,
  uniqueEntityCount,
  entitiesByType,
  tabs,
  tableLinkExtend,
  gapAnalysisId,
  getTypeLabel,
  snapshotDate,
  snapshotName,
  historicalSubtitle,
}) => {
  const { t, i18n } = useTranslation(['gap-analysis', 'common', 'dashboard']);
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [entityOpenIds, setEntityOpenIds] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleExportPdf = () => {
    setExportOpen(false);
    window.print();
  };

  const handleExportExcel = () => {
    setExportOpen(false);
    const rows = [[t('dashboard:gapAnalysis.table.gapAnalysis'), t('common:table.name'), '%']];
    (frameworkData || []).forEach(f => rows.push([f.label, '', f.value]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'gap-analysis'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleEntityRow = id =>
    setEntityOpenIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const completedControls = Math.round((overallProgress / 100) * totalControls);

  const linkedProgress = totalControls > 0
    ? Math.round((linkedControls / totalControls) * 100)
    : 0;

  const maturityTotal = maturityData.reduce((s, d) => s + d.value, 0);
  const totalStatusCount = statusDonutData.reduce((s, d) => s + d.value, 0);
  const statusSegments = computeDonut(statusDonutData, 38);
  const entitySegments = computeDonut(entityDonutData, 38);

  const currentTabEntities = entitiesByType[tabs[tabIndex]?.id] || [];

  const fmtSnapshot = iso => {
    if (!iso) return '';
    const locale = SNAP_LOCALE_MAP[i18n.language] ?? 'pt-BR';
    return new Date(iso).toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const historyTitle = snapshotDate && snapshotName
    ? t('dashboard:gapAnalysis.show.historicalUpTo', { name: snapshotName })
    : t('gap-analysis:dashboard.history');

  return (
    <Container>
      {/* ── Page header ── */}
      <PageHeader
        title={title}
        segments={segments}
        actions={
        <HeaderActions>
          {snapshotDate && (
            <ExportWrap ref={exportRef}>
              <ExportBtn type="button" onClick={() => setExportOpen(v => !v)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {t('dashboard:gapAnalysis.show.export')}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </ExportBtn>
              <ExportDrop $open={exportOpen}>
                <ExportItem onClick={handleExportPdf}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {t('dashboard:gapAnalysis.show.exportPdf')}
                </ExportItem>
                <ExportItem onClick={handleExportExcel}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18M3 15h18M9 3v18"/>
                  </svg>
                  {t('dashboard:gapAnalysis.show.exportExcel')}
                </ExportItem>
              </ExportDrop>
            </ExportWrap>
          )}
          <BackBtn type="button" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {backLabel}
          </BackBtn>
        </HeaderActions>
        }
      />

      {/* ── Snapshot banner (report Show page only) ── */}
      {snapshotDate && (
        <SnapBanner>
          <SnapLeft>
            <SnapIconBox>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-capri)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <polyline points="3 9 21 9"/>
                <polyline points="3 15 21 15"/>
                <polyline points="9 3 9 21"/>
                <polyline points="15 3 15 21"/>
              </svg>
            </SnapIconBox>
            <div>
              <SnapBadge>{t('dashboard:gapAnalysis.show.snapshot')}</SnapBadge>
              <SnapName>{title}</SnapName>
            </div>
          </SnapLeft>
          <SnapRight>
            <SnapDateLabel>{t('dashboard:gapAnalysis.table.generatedAt')}</SnapDateLabel>
            <SnapDateValue>{fmtSnapshot(snapshotDate)}</SnapDateValue>
          </SnapRight>
        </SnapBanner>
      )}

      {/* ── Row 1: KPI cards + history chart ── */}
      <DashGrid1>
        <ColStack>
          <Card>
            <SectionLabel>{t('gap-analysis:dashboard.progressTitle')}</SectionLabel>
            <KpiNumber $color={BLUE}>{overallProgress}%</KpiNumber>
            <KpiTrack>
              <KpiFill $pct={overallProgress} $gradient={`linear-gradient(90deg, ${BLUE_DEEP}, ${CYAN})`} />
            </KpiTrack>
            <KpiSubLabel>
              {t('gap-analysis:dashboard.progressDesc', { progress: completedControls, total: totalControls })}
            </KpiSubLabel>
          </Card>
          <Card>
            <SectionLabel>{t('gap-analysis:dashboard.linkedControlsTitle')}</SectionLabel>
            <KpiNumber $color={GREEN}>{linkedControls}</KpiNumber>
            <KpiTrack>
              <KpiFill $pct={linkedProgress} $gradient={`linear-gradient(90deg, ${GREEN}, ${CYAN})`} />
            </KpiTrack>
            <KpiSubLabel>
              {t('gap-analysis:dashboard.linkedControlsDesc', { linked: linkedControls, total: totalControls })}
            </KpiSubLabel>
          </Card>
        </ColStack>
        <ChartLine
          title={historyTitle}
          cardSubtitle={snapshotDate ? t('dashboard:gapAnalysis.show.frozenData') : historicalSubtitle}
          data={historicalProgress}
          tooltipLabel={t('gap-analysis:dashboard.tooltipProgress')}
          color={BLUE}
        />
      </DashGrid1>

      {/* ── Row 2: Framework bars + Status donut + Maturity ── */}
      <DashGrid2>
        <Card>
          <CardTitle>{t('gap-analysis:dashboard.progressByFramework')}</CardTitle>
          <BarList>
            {frameworkData.map(({ label, value }) => (
              <div key={label}>
                <BarRowHead>
                  <BarRowName>{label}</BarRowName>
                  <BarRowPct>{value}%</BarRowPct>
                </BarRowHead>
                <BarTrack><BarGradFill $pct={value} /></BarTrack>
              </div>
            ))}
          </BarList>
        </Card>

        <Card>
          <CardTitle>{t('gap-analysis:dashboard.statusDistribution')}</CardTitle>
          <DonutWrap>
            <svg width="90" height="90" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
              <circle cx="50" cy="50" r="38" fill="none" stroke={DIVIDER} strokeWidth="16" />
              {statusSegments.map(seg => (
                <circle
                  key={seg.name}
                  cx="50" cy="50" r="38"
                  fill="none"
                  stroke={seg.color}
                  strokeWidth="16"
                  strokeDasharray={seg.da}
                  strokeDashoffset={seg.off}
                  transform="rotate(-90 50 50)"
                />
              ))}
              <text x="50" y="46" textAnchor="middle" fontFamily="Rubik" fontSize="15" fontWeight="700" fill={INK}>
                {totalStatusCount}
              </text>
              <text x="50" y="58" textAnchor="middle" fontFamily="Lato" fontSize="9" fill={MUTED}>
                {t('gap-analysis:dashboard.controlsLabel')}
              </text>
            </svg>
            <DonutLegend>
              {statusDonutData.map(d => (
                <LegendRow key={d.name}>
                  <LegendDot $color={d.color} />
                  <LegendText>{d.name} ({d.value})</LegendText>
                </LegendRow>
              ))}
            </DonutLegend>
          </DonutWrap>
        </Card>

        <Card>
          <CardTitle>{t('gap-analysis:dashboard.maturityLevelTitle')}</CardTitle>
          <MaturityList>
            {maturityData.map(({ name, value, color }) => (
              <div key={name}>
                <MatRowHead>
                  <MatLabel>{name}</MatLabel>
                  <MatCount $color={color}>{value}</MatCount>
                </MatRowHead>
                <MatTrack>
                  <MatFill $pct={maturityTotal > 0 ? (value / maturityTotal) * 100 : 0} $color={color} />
                </MatTrack>
              </div>
            ))}
          </MaturityList>
        </Card>
      </DashGrid2>

      {/* ── Row 3: Entity donut + Stacked bars + Unique + Entity progress ── */}
      <DashGrid3>
        <Card>
          <CardTitle>{t('gap-analysis:dashboard.controlsByEntity')}</CardTitle>
          <DonutWrap>
            <svg width="90" height="90" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
              <circle cx="50" cy="50" r="38" fill="none" stroke={DIVIDER} strokeWidth="16" />
              {entitySegments.map(seg => (
                <circle
                  key={seg.name}
                  cx="50" cy="50" r="38"
                  fill="none"
                  stroke={seg.color}
                  strokeWidth="16"
                  strokeDasharray={seg.da}
                  strokeDashoffset={seg.off}
                  transform="rotate(-90 50 50)"
                />
              ))}
              <text x="50" y="47" textAnchor="middle" fontFamily="Rubik" fontSize="16" fontWeight="700" fill={INK}>
                {linkedControls}
              </text>
              <text x="50" y="57" textAnchor="middle" fontFamily="Lato" fontSize="9" fill={MUTED}>
                {t('gap-analysis:dashboard.linksLabel')}
              </text>
            </svg>
            <DonutLegend>
              {entityDonutData.map(d => (
                <LegendRow key={d.name}>
                  <LegendDot $color={d.color} />
                  <LegendText>{d.name} ({d.value})</LegendText>
                </LegendRow>
              ))}
            </DonutLegend>
          </DonutWrap>
        </Card>

        <Card>
          <CardTitle>{t('gap-analysis:dashboard.statusByEntity')}</CardTitle>
          <StackList>
            {stackedData.map(({ name, statuses }) => {
              const total = Object.values(statuses).reduce((s, c) => s + (c || 0), 0);
              return (
                <div key={name}>
                  <StackRowHead>
                    <StackName>{name}</StackName>
                    <StackCount>{total}</StackCount>
                  </StackRowHead>
                  <StackBarWrap>
                    {Object.keys(statuses).map(sKey => {
                      const cnt = statuses[sKey] || 0;
                      if (cnt === 0) return null;
                      return (
                        <StackSeg
                          key={sKey}
                          $flex={cnt}
                          $color={statusControl[sKey]?.color || MUTED}
                        />
                      );
                    })}
                  </StackBarWrap>
                </div>
              );
            })}
          </StackList>
          <StackLegend>
            {Object.keys(statusControl).map(key => (
              <StackLegendItem key={key}>
                <StackDot $color={statusControl[key].color} />
                {getStatusLabel(key, t)}
              </StackLegendItem>
            ))}
          </StackLegend>
        </Card>

        <ColStack>
          <Card>
            <SectionLabel>{t('gap-analysis:dashboard.uniqueEntities')}</SectionLabel>
            <UniqueNumber>{uniqueEntityCount ?? 0}</UniqueNumber>
            <UniqueSubLabel>{t('gap-analysis:dashboard.uniqueEntitiesDesc')}</UniqueSubLabel>
          </Card>
          <Card>
            <EpTitle>{t('gap-analysis:dashboard.entityProgress')}</EpTitle>
            <EpList>
              {entityProgressData.map(({ label, value }) => (
                <EpRow key={label}>
                  <EpLabel>{label}</EpLabel>
                  <EpRight>
                    <EpMiniTrack><EpMiniFill $pct={value} /></EpMiniTrack>
                    <EpPct>{Math.round(Number(value) || 0)}%</EpPct>
                  </EpRight>
                </EpRow>
              ))}
            </EpList>
          </Card>
        </ColStack>
      </DashGrid3>

      {/* ── Row 4: Entity type tabs + expandable entity table ── */}
      <TableCard>
        <TableCardHead>
          <TableCardTitle>{t('gap-analysis:dashboard.entityTableTitle')}</TableCardTitle>
          <EntityTabsWrap>
            {tabs.map((tab, i) => (
              <EntityTabBtn
                key={tab.id}
                $active={tabIndex === i}
                type="button"
                onClick={() => { setTabIndex(i); setEntityOpenIds([]); }}
              >
                {tab.label}
              </EntityTabBtn>
            ))}
          </EntityTabsWrap>
        </TableCardHead>
        <EtTable>
          <thead>
            <tr>
              <EtTh style={{ width: 52 }} />
              <EtTh>{t('common:labels.name')}</EtTh>
              <EtTh>{t('common:labels.type')}</EtTh>
              <EtTh style={{ minWidth: 180 }}>{t('gap-analysis:table.progress')}</EtTh>
            </tr>
          </thead>
          <tbody>
            {currentTabEntities.map(entity => {
              const rowId = entity.entity_id ?? entity._id ?? entity.entity_name;
              const isOpen = entityOpenIds.includes(rowId);
              const pct = Math.min(Math.round(Number(entity.progresso) || 0), 100);
              const controls = entity.entities || [];
              return (
                <React.Fragment key={rowId}>
                  <EtParentTr onClick={() => toggleEntityRow(rowId)}>
                    <EtTd style={{ width: 52, textAlign: 'center', paddingLeft: 16 }}>
                      <EtExpandBtn
                        type="button"
                        onClick={e => { e.stopPropagation(); toggleEntityRow(rowId); }}
                      >
                        <svg
                          width="11" height="11" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2.5"
                          strokeLinecap="round" strokeLinejoin="round"
                          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms', display: 'block' }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </EtExpandBtn>
                    </EtTd>
                    <EtTd style={{ fontWeight: 500 }}>{entity.entity_name}</EtTd>
                    <EtTd>
                      <EtTypeChip>{getTypeLabel(entity.entity_type)}</EtTypeChip>
                    </EtTd>
                    <EtTd>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <EtProgressTrack>
                          <EtProgressFill $pct={pct} />
                        </EtProgressTrack>
                        <EtProgressPct>{pct}%</EtProgressPct>
                      </div>
                    </EtTd>
                  </EtParentTr>
                  {isOpen && controls.length > 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0, borderBottom: `1px solid ${DIVIDER}` }}>
                        <SubTable>
                          <thead>
                            <SubHeadTr>
                              <SubTh style={{ paddingLeft: 48 }}>{t('gap-analysis:table.control')}</SubTh>
                              <SubTh>{t('gap-analysis:table.domain')}</SubTh>
                              <SubTh style={{ textAlign: 'center' }}>{t('gap-analysis:table.controlWeight')}</SubTh>
                              <SubTh style={{ textAlign: 'center' }}>{t('common:labels.status')}</SubTh>
                              <SubTh>{t('gap-analysis:table.maturityLevel')}</SubTh>
                              <SubTh style={{ textAlign: 'center', paddingRight: 16 }}>{t('gap-analysis:table.actions')}</SubTh>
                            </SubHeadTr>
                          </thead>
                          <tbody>
                            {controls.map(ctrl => {
                              const ctrlId = ctrl._id ?? ctrl.scfId;
                              const sc = statusControl[ctrl.status];
                              return (
                                <SubDataTr key={ctrlId}>
                                  <SubTd style={{ paddingLeft: 48 }}>{ctrl.scfControl}</SubTd>
                                  <SubTd>
                                    <SubDomainChip>{translateDomain(t, ctrl.scfDomain)}</SubDomainChip>
                                  </SubTd>
                                  <SubTd style={{ textAlign: 'center', fontFamily: "'Rubik', sans-serif", fontWeight: 700 }}>
                                    {ctrl.controlWeighting}
                                  </SubTd>
                                  <SubTd style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                                      <SubStatusDot $color={sc?.color ?? MUTED} />
                                      <span style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 11, color: MUTED }}>
                                        {sc ? t(sc.labelKey) : ctrl.status}
                                      </span>
                                    </div>
                                  </SubTd>
                                  <SubTd style={{ fontFamily: "'Maven Pro', sans-serif", fontSize: 11, color: MUTED }}>
                                    {maturityLevelKeys[ctrl.maturity_level]
                                      ? t(maturityLevelKeys[ctrl.maturity_level])
                                      : ctrl.maturity_level}
                                  </SubTd>
                                  <SubTd style={{ textAlign: 'center', paddingRight: 16 }}>
                                    <SubViewBtn
                                      type="button"
                                      title={t('common:tooltips.view')}
                                      onClick={() => navigate(`/${tableLinkExtend}/${ctrlId}${gapAnalysisId ? `?gapanalysis_id=${gapAnalysisId}` : ''}`)}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </SubViewBtn>
                                  </SubTd>
                                </SubDataTr>
                              );
                            })}
                          </tbody>
                        </SubTable>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </EtTable>
      </TableCard>
    </Container>
  );
};
